import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Truck, Navigation, Search, Filter } from 'lucide-react';
import { SupplierLayout } from '../../components/SupplierLayout';
import { MOCK_TRUCKS, MOCK_DRIVERS } from '../../lib/supplierStore';
export function SupplierTracking() {
  const [filter, setFilter] = useState('All');
  const activeTrucks = MOCK_TRUCKS.filter(t => t.location);
  return <SupplierLayout data-id="element-4416">
      <div className="h-[calc(100vh-8rem)] flex flex-col lg:flex-row gap-6" data-id="element-4417">
        {/* Map Area */}
        <div className="flex-1 rounded-3xl overflow-hidden relative bg-gray-200 border border-gray-200 shadow-sm min-h-[400px]" data-id="element-4418">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=1600')] bg-cover bg-center opacity-50 grayscale" data-id="element-4419" />
          <div className="absolute inset-0 bg-gray-900/5 mix-blend-multiply" data-id="element-4420" />

          {/* Map Controls */}
          <div className="absolute top-4 left-4 right-4 flex gap-2 z-10" data-id="element-4421">
            <div className="bg-white/90 backdrop-blur px-4 py-2.5 rounded-xl shadow-sm border border-gray-100 flex items-center gap-2 font-bold text-sm text-gray-700" data-id="element-4422">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" data-id="element-4423" />
              {activeTrucks.length} Trucks Live
            </div>
          </div>

          {/* Pins */}
          {activeTrucks.map((truck, i) => <motion.div key={truck.id} initial={{
          scale: 0,
          y: 10
        }} animate={{
          scale: 1,
          y: 0
        }} transition={{
          delay: i * 0.1
        }} className="absolute" style={{
          left: `${(truck.location!.lng - 55.0) * 150}%`,
          top: `${(25.4 - truck.location!.lat) * 150}%`,
          transform: 'translate(-50%, -100%)'
        }} data-id="element-4424">
              <div className="relative flex flex-col items-center group cursor-pointer" data-id="element-4425">
                {truck.status === 'Active' && <div className="absolute -inset-2 rounded-full bg-brand-500/20 animate-ping" data-id="element-4426" />}
                <div className={`relative w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-2 border-white z-10 ${truck.status === 'Active' ? 'orange-gradient-bg' : 'bg-gray-800'}`} data-id="element-4427">
                  <Truck className="w-4 h-4 text-white" data-id="element-4428" />
                </div>
                <div className="w-1.5 h-2 bg-gray-900/40 rounded-full mt-0.5" data-id="element-4429" />

                {/* Tooltip */}
                <div className="absolute bottom-full mb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs font-bold px-3 py-1.5 rounded-lg whitespace-nowrap pointer-events-none" data-id="element-4430">
                  {truck.plate} • {truck.status}
                </div>
              </div>
            </motion.div>)}
        </div>

        {/* List Panel */}
        <div className="w-full lg:w-96 flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden shrink-0" data-id="element-4431">
          <div className="p-4 border-b border-gray-100" data-id="element-4432">
            <div className="relative mb-3" data-id="element-4433">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-4434" />
              <input type="text" placeholder="Search plate or driver..." className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:border-brand-300" data-id="element-4435" />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1" data-id="element-4436">
              {['All', 'Active', 'Idle', 'Maintenance'].map(f => <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors ${filter === f ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-4437">
                  {f}
                </button>)}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2" data-id="element-4438">
            {MOCK_TRUCKS.filter(t => filter === 'All' || t.status === filter).map(truck => {
            const driver = MOCK_DRIVERS.find(d => d.id === truck.assignedDriverId);
            return <div key={truck.id} className="p-3 hover:bg-gray-50 rounded-xl transition-colors border border-transparent hover:border-gray-100 mb-1 flex items-center justify-between group" data-id="element-4439">
                  <div className="flex items-center gap-3" data-id="element-4440">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${truck.status === 'Active' ? 'bg-brand-50 text-brand-600' : 'bg-gray-100 text-gray-500'}`} data-id="element-4441">
                      <Truck className="w-5 h-5" data-id="element-4442" />
                    </div>
                    <div data-id="element-4443">
                      <p className="font-bold text-gray-900 text-sm" data-id="element-4444">
                        {truck.plate}
                      </p>
                      <p className="text-[11px] text-gray-500" data-id="element-4445">
                        {driver ? driver.name : 'Unassigned'} • {truck.type}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1" data-id="element-4446">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${truck.status === 'Active' ? 'bg-green-100 text-green-700' : truck.status === 'Idle' ? 'bg-gray-100 text-gray-600' : 'bg-red-100 text-red-700'}`} data-id="element-4447">
                      {truck.status}
                    </span>
                    {truck.location && <button className="text-[10px] font-bold text-brand-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1" data-id="element-4448">
                        <MapPin className="w-3 h-3" data-id="element-4449" /> Locate
                      </button>}
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </SupplierLayout>;
}